import { useEffect, useLayoutEffect, useRef, useState } from "react";

import type { SectionMenu } from "components/ScrollToSectionMenu";

const OVERFLOW_BUTTON_WIDTH = 50;
const RIGHT_PADDING = 15;

export const useOverflowingSectionMenus = (sectionMenus: Record<string, SectionMenu>) => {
  const resizeObserver = useRef<ResizeObserver | null>(null);
  const [overflowingSectionMenus, setOverflowingSectionMenus] = useState<SectionMenu[]>([]);
  const [visibleSectionMenus, setVisibleSectionMenus] = useState<SectionMenu[]>([]);
  const [activeSectionMenu, setActiveSectionMenu] = useState<string>("");
  const [canSpy, setCanSpy] = useState(true);
  const [isSectionMenuClicked, setIsSectionClicked] = useState(false);
  const menuContainerRef = useRef<HTMLDivElement>(null);
  const sectionMenuClickTimeoutRef = useRef<number>(0);

  const calculateVisibleSectionMenus = () => {
    if (!menuContainerRef.current) return;

    const containerWidth = menuContainerRef.current.offsetWidth;
    if (containerWidth === 0) return;

    const visible: SectionMenu[] = [];
    const overflowing: SectionMenu[] = [];
    let availableWidth = containerWidth - OVERFLOW_BUTTON_WIDTH - RIGHT_PADDING;

    Object.values(sectionMenus).forEach(sectionMenu => {
      if (!sectionMenu.count && !sectionMenu.showEmpty) return;

      const span = document.createElement("span");
      span.style.visibility = "hidden";
      span.className = "item";
      span.textContent = (sectionMenu.icon ? "mmmm" : sectionMenu.title) + (sectionMenu.count ? ` (${sectionMenu.count})` : "");
      document.body.appendChild(span);

      const itemWidth = Math.min(span.offsetWidth, 300);
      document.body.removeChild(span);

      if (availableWidth - itemWidth >= 0) {
        availableWidth -= itemWidth;
        visible.push(sectionMenu);
      } else overflowing.push(sectionMenu);
    });

    setVisibleSectionMenus(visible);
    setOverflowingSectionMenus(overflowing);
  };

  const toggleTabClickActivity = () => {
    setIsSectionClicked(true);
    clearTimeout(sectionMenuClickTimeoutRef.current);

    sectionMenuClickTimeoutRef.current = window.setTimeout(() => setIsSectionClicked(false), 500);
  };

  const handleSectionMenuClick = (id: string) => {
    if (id === activeSectionMenu) return;

    toggleTabClickActivity();
    const overflowIndex = overflowingSectionMenus.findIndex(e => e.id === id);

    if (overflowIndex < 0) {
      calculateVisibleSectionMenus(); // why ?
      setActiveSectionMenu(id);
      setCanSpy(false);
      return;
    }

    const lastVisibleSection = visibleSectionMenus[visibleSectionMenus.length - 1];

    setVisibleSectionMenus(prev => {
      const visibleSectionMenus = [...prev];
      visibleSectionMenus[visibleSectionMenus.length - 1] = overflowingSectionMenus[overflowIndex];
      return visibleSectionMenus;
    });

    setOverflowingSectionMenus(prev => {
      const overflowingSectionMenus = [...prev];
      overflowingSectionMenus[overflowIndex] = lastVisibleSection;
      return overflowingSectionMenus;
    });

    setActiveSectionMenu(id);
    setCanSpy(false);
  };

  useEffect(() => {
    const contentContainer = document.querySelector<HTMLDivElement>(".content-container");
    const handleScroll = () => {
      if (isSectionMenuClicked) return setCanSpy(false);
      setCanSpy(true);

      if (!contentContainer) return;

      const isAtBottomOfThePage = contentContainer.scrollTop + contentContainer.clientHeight >= contentContainer.scrollHeight - 10;

      if (isAtBottomOfThePage && overflowingSectionMenus.length > 0) setActiveSectionMenu(overflowingSectionMenus[overflowingSectionMenus.length - 1].id);
      else if (isAtBottomOfThePage && visibleSectionMenus.length > 0) setActiveSectionMenu(visibleSectionMenus[visibleSectionMenus.length - 1].id);
    };

    contentContainer?.addEventListener("scroll", handleScroll);
    return () => contentContainer?.removeEventListener("scroll", handleScroll);
  }, [isSectionMenuClicked, visibleSectionMenus]);

  useLayoutEffect(() => {
    if (!menuContainerRef.current) return;

    resizeObserver.current = new ResizeObserver(calculateVisibleSectionMenus);
    resizeObserver.current.observe(menuContainerRef.current);

    return () => {
      if (resizeObserver.current) {
        resizeObserver.current.disconnect();
      }
    };
  }, [sectionMenus]);

  useEffect(() => {
    calculateVisibleSectionMenus();
  }, [sectionMenus]);

  return {
    canSpy,
    setActiveSectionMenu,
    activeSectionMenu,
    handleSectionMenuClick,
    menuContainerRef,
    visibleSectionMenus,
    overflowingSectionMenus
  };
};
